import { Required } from "@countdown/theme";
import { type ReactElement, type SyntheticEvent, useState } from "react";

import { PageHeading } from "@/components/PageHeading";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  type ParticipantRecord,
  ROLE_MISSING,
  eraseParticipant,
  lookupParticipant,
  participantRef,
} from "@/lib/api";
import { can, formatDateTime } from "@/lib/labels";

/**
 * Participant records and erasure (concept 10.6).
 *
 * Counts only, like the CLI's `participant show`. The address is typed, sent in
 * a POST body and cleared after a hit; it is never logged and no answer repeats
 * it. Every record shown writes an audit row on the server.
 *
 * An address can stand behind several participants, an account and the route
 * without one, so a search shows every record it finds, each with its own
 * erasure. The erasure sends only the id of the record it sits under, so the
 * irreversible step cannot land on a mistyped address or on the other record.
 *
 * Tenant admins only, like its navigation entry. The page loads nothing up
 * front, so no 403 could tell another role before the search, and that search
 * would already have sent the typed address. The server enforces the role
 * either way.
 */
export function ParticipantsPage({
  granted,
}: {
  readonly granted: readonly string[];
}): ReactElement {
  const [query, setQuery] = useState("");
  const [records, setRecords] = useState<readonly ParticipantRecord[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const search = (event: SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const value = query.trim();
    if (value === "") {
      return;
    }
    setBusy(true);
    setMessage(null);
    setRecords([]);
    lookupParticipant(participantRef(value))
      .then((result) => {
        if (result.ok) {
          setRecords(result.records);
          setQuery("");
        } else {
          setMessage(result.message);
        }
      })
      .catch((error: unknown) => {
        console.error("[ParticipantsPage] lookup failed", error);
        setMessage("Die Suche ist fehlgeschlagen.");
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const erase = (participantId: string, reason: string): void => {
    setBusy(true);
    setMessage(null);
    eraseParticipant(participantId, reason)
      .then((result) => {
        if (result.ok) {
          setRecords((current) =>
            current.filter((record) => record.participant_id !== participantId),
          );
          setMessage("Gelöscht.");
        } else {
          setMessage(result.message);
        }
      })
      .catch((error: unknown) => {
        console.error("[ParticipantsPage] erase failed", error);
        setMessage("Das Löschen ist fehlgeschlagen.");
      })
      .finally(() => {
        setBusy(false);
      });
  };

  if (!can(granted, "participants")) {
    return (
      <>
        <PageHeading title="Teilnehmer" />
        <p className="text-sm text-muted-foreground">{ROLE_MISSING}</p>
      </>
    );
  }

  return (
    <>
      <PageHeading title="Teilnehmer" />

      <form className="flex max-w-2xl flex-wrap items-end gap-2" onSubmit={search}>
        <div className="min-w-64 flex-1">
          <label className="block text-sm font-medium" htmlFor="teilnehmer">
            E-Mail-Adresse oder Kennung
          </label>
          <input
            className="mt-1 w-full border border-input bg-background px-3 py-2 text-sm"
            id="teilnehmer"
            autoComplete="off"
            maxLength={320}
            value={query}
            disabled={busy}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
        </div>
        <Button type="submit" disabled={busy || query.trim() === ""}>
          Suchen
        </Button>
      </form>

      {message !== null ? (
        <div
          className="mt-6 max-w-2xl border border-border bg-surface-sunken px-4 py-3 text-sm"
          role="status"
          aria-live="polite"
        >
          {message}
        </div>
      ) : null}

      {records.length > 1 ? (
        <p className="mt-6 max-w-2xl text-sm">
          Hinter dieser E-Mail stehen {records.length} Teilnehmer.
        </p>
      ) : null}

      {records.map((record) => (
        <RecordSection
          key={record.participant_id}
          record={record}
          busy={busy}
          onErase={erase}
        />
      ))}
    </>
  );
}

/** One record with its own erasure, so a reason typed under one never erases the other. */
function RecordSection({
  record,
  busy,
  onErase,
}: {
  readonly record: ParticipantRecord;
  readonly busy: boolean;
  readonly onErase: (participantId: string, reason: string) => void;
}): ReactElement {
  const [reason, setReason] = useState("");
  const reasonId = `loeschgrund-${record.participant_id}`;

  return (
    <section className="mt-8 max-w-2xl">
      <h2 className="flex flex-wrap items-center gap-2 text-lg font-bold">
        Bestand
        {/* The participant of a test run from the preview (D21), counted nowhere. */}
        {record.is_test ? <Badge variant="outline">Test</Badge> : null}
      </h2>
      <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
        <dt>Kennung</dt>
        <dd className="break-all font-mono text-xs">{record.participant_id}</dd>
        <dt>Sperre</dt>
        <dd>
          {record.blocked_at === null ? (
            "keine"
          ) : (
            <Badge variant="destructive">
              gesperrt seit {formatDateTime(record.blocked_at)}
              {record.block_reason ? `: ${record.block_reason}` : ""}
            </Badge>
          )}
        </dd>
        <dt>Teilnahmen</dt>
        <dd>{record.participations}</dd>
        <dt>Einwilligungen</dt>
        <dd>{record.consents}</dd>
        <dt>Zustimmungen zu Teilnahmebedingungen</dt>
        <dd>{record.terms_acceptances}</dd>
        <dt>Eingereihte Mails</dt>
        <dd>{record.queued_mail}</dd>
        <dt>Übermittelte Leads</dt>
        <dd>{record.leads}</dd>
        <dt>Gewinnzuteilungen</dt>
        <dd>{record.prize_awards}</dd>
      </dl>

      {record.erasure_obstacles.length > 0 ? (
        <div className="mt-4 text-sm">
          <p className="font-medium">Einer Löschung steht entgegen:</p>
          <ul className="mt-1 list-disc pl-5">
            {record.erasure_obstacles.map((obstacle) => (
              <li key={obstacle}>{obstacle}</li>
            ))}
          </ul>
        </div>
      ) : (
        <details className="mt-4">
          <summary className="cursor-pointer text-sm font-medium">Löschen</summary>
          <form
            className="mt-3"
            onSubmit={(event) => {
              event.preventDefault();
              onErase(record.participant_id, reason.trim());
            }}
          >
            <label className="block text-sm font-medium" htmlFor={reasonId}>
              Begründung
              <Required />
            </label>
            <input
              className="mt-1 w-full border border-input bg-background px-3 py-2 text-sm"
              id={reasonId}
              required
              minLength={3}
              maxLength={500}
              value={reason}
              disabled={busy}
              onChange={(event) => {
                setReason(event.target.value);
              }}
            />
            <Button
              className="mt-3"
              type="submit"
              variant="destructive"
              disabled={busy || reason.trim().length < 3}
            >
              Endgültig löschen
            </Button>
          </form>
        </details>
      )}
    </section>
  );
}
